import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject, EMPTY, catchError, combineLatest, first, map, shareReplay, tap } from 'rxjs';
import { Constants } from 'src/app/core/constants';
import { CustomFile } from 'src/app/core/models/customFile.interface';
import { AssignmentFormModel } from 'src/app/core/models/project/assignment/assignment-form.model';
import { AssignmentForCreate } from 'src/app/core/models/project/assignment/assignment.model';
import {
  AttachmentWithDetails,
} from 'src/app/core/models/project/attachment/attachment.interface';
import { EmployeeService } from 'src/app/core/services/employee.service';
import { FileService } from 'src/app/core/services/file.service';
import { AssignmentService } from 'src/app/core/services/assignment.service';
import { TariffService } from 'src/app/core/services/tariff.service';
import { AssignmentChangeHub } from 'src/app/core/signalr-hubs/assignment-change.hub';
import { Convertor } from 'src/app/core/utils/convertor';
import { Util } from 'src/app/core/utils/util';
import { EditableAssignmentComponent } from '../editable-assignment.component';

@Component({
    selector: 'app-project-create-assignment',
    templateUrl: './create-assignment.component.html',
    styleUrls: ['./create-assignment.component.scss'],
    providers: [AssignmentFormModel],
    standalone: false
})
export class CreateAssignmentComponent
  extends EditableAssignmentComponent
  implements OnInit, OnChanges
{
  private readonly route = inject(ActivatedRoute);
  readonly employeeService = inject(EmployeeService);
  private readonly assignmentService = inject(AssignmentService);
  private readonly assignmentChangeHub = inject(AssignmentChangeHub);
  private readonly tariffService = inject(TariffService);

  @Input() public override companyId = '';
  @Input() public override projectId = '';
  @Output() public cancelled = new EventEmitter<void>();
  @Output() public created = new EventEmitter<string>();

  public attachmentsPath = '';
  public assignmentId = Util.generateGUID();
  public files: AttachmentWithDetails[] = [];
  private readonly selectedFilesSubject$ = new BehaviorSubject<AttachmentWithDetails[]>([]);
  public readonly storageUsage$ = combineLatest([
    this.tariffService.companyQuotas$,
    this.selectedFilesSubject$,
  ]).pipe(
    map(([companyQuotas, selectedFiles]) => {
      const quotaKey = Convertor.pascalToScreamingSnakeCase(
        Constants.QuotaKeys.s3Size,
      );
      const storageQuota = companyQuotas.find((q) => q.key === quotaKey);
      const limit = storageQuota?.limit ?? 0;
      const used = storageQuota?.used ?? 0;
      const staged = selectedFiles.reduce((sum, file) => sum + (file.fileSize ?? 0), 0);
      const totalUsed = used + staged;
      const available = Math.max(limit - totalUsed, 0);
      const usedPercent = limit > 0 ? Math.round((Math.min(totalUsed, limit) / limit) * 1000) / 10 : 0;

      return {
        limit,
        used: totalUsed,
        usedBase: used,
        staged,
        available,
        usedPercent,
      };
    }),
    shareReplay(1),
  );
  private initializedContextKey = '';

  public ngOnInit(): void {
    this.initializeContext();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['companyId'] || changes['projectId']) {
      this.initializeContext();
    }
  }

  public submitForm(): void {
    if (this.assignmentFormModel.form.invalid) {
      this.assignmentFormModel.form.markAllAsTouched();
      return;
    }

    const deadlineDate = this.deadlineFormControl.value
      ? Util.getFormattedStringDate(new Date(this.deadlineFormControl.value))
      : undefined;

    const assignment: AssignmentForCreate = {
      id: this.assignmentId,
      title: this.titleFormControl.value!,
      deadline: deadlineDate,
      description: this.descriptionFormControl.value || undefined,
      attachments: this.files,
      employeeId: this.assignmentEmployee?.id,
    };

    this.assignmentService
      .createAssignment(assignment)
      .pipe(
        first(),
        tap((assignmentId) => {
          this.assignmentChangeHub.sendAssignmentChangeNotification(assignmentId);
          this.resetLocalDraft();
          this.created.emit(assignmentId);
        }),
        catchError((error) => {
          console.log(error);
          return EMPTY;
        }),
      )
      .subscribe();
  }

  public onUploadFile(event: CustomFile): void {
    const fileId = Util.getGuidFromTitle(event.title);

    if (!fileId) {
      return;
    }

    const nextFile: AttachmentWithDetails = {
      id: fileId,
      title: event.file.name,
      file: event.file,
      fileSize: event.file.size,
      companyId: this.companyId,
      isFolder: false,
      imageSrc: Util.getAttachmentImage(event.file.name),
      extensionName: Util.getExtensionFromFileTitle(event.file.name),
      fileActionName: 'download',
    };

    this.files = [...this.files, nextFile];
    this.selectedFilesSubject$.next(this.files);
  }

  public onDeleteFile(event: CustomFile): void {
    const fileId = Util.getGuidFromTitle(event.title);

    if (!fileId) {
      return;
    }

    this.files = this.files.filter((file) => file.id !== fileId);
    this.selectedFilesSubject$.next(this.files);
  }

  public removeFile(file: AttachmentWithDetails): void {
    this.fileService
      .delete(`${this.attachmentsPath}/${Util.getFileFullName(file)}`)
      .pipe(
        first(),
        tap(() => {
          this.files = this.files.filter((item) => item.id !== file.id);
          this.selectedFilesSubject$.next(this.files);
        }),
        catchError((error) => {
          console.log(error);
          return EMPTY;
        }),
      )
      .subscribe();
  }

  public onCancel(): void {
    this.resetLocalDraft();
    this.cancelled.emit();
  }

  private initializeContext(): void {
    const routeCompanyId =
      this.route.parent?.parent?.snapshot.params['id'] ??
      this.route.parent?.snapshot.params['id'] ??
      '';
    const routeProjectId =
      this.route.snapshot.params['projectId'] ??
      this.route.snapshot.params['id'] ??
      '';

    const nextCompanyId = this.companyId || routeCompanyId;
    const nextProjectId = this.projectId || routeProjectId;

    if (!nextCompanyId || !nextProjectId) {
      return;
    }

    const nextContextKey = `${nextCompanyId}:${nextProjectId}`;
    if (this.initializedContextKey === nextContextKey) {
      return;
    }

    this.companyId = nextCompanyId;
    this.projectId = nextProjectId;
    this.initializedContextKey = nextContextKey;

    this.resetLocalDraft();
    this.assignmentId = Util.generateGUID();
    this.attachmentsPath = `companies/${this.companyId}/projects/${this.projectId}/assignments/${this.assignmentId}/files`;

    this.assignmentService.setAssignmentsUrl(this.projectId);
    this.employeeService.setEmployeesUrl(this.companyId);
    this.tariffService.setCompanyId(this.companyId);

    this.employeeService
      .getProjectEmployeesLookup(this.projectId)
      .pipe(
        first(),
        tap((employees) => this.projectEmployees$.next(employees)),
        catchError((error) => {
          console.log(error);
          return EMPTY;
        }),
      )
      .subscribe();
  }

  private resetLocalDraft(): void {
    this.files = [];
    this.selectedFilesSubject$.next([]);
    this.resetAssignmentDraft();
    this.deadlineFormControl.setValue(null);
    this.descriptionFormControl.setValue('');
    this.titleFormControl.setValue('');
  }
}
